import { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { useResponsive, useRouteHook } from '@shared/hooks';
import { PaletteUtils, RoutePaths } from '@shared/utils';
import { DetailsEditor, Password } from './widgets';
import { Tabs } from '@shared/widgets';
import PersonalSvg from '@assets/icons/profile/personal.svg';
import PasswordSvg from '@assets/icons/profile/password.svg';

enum TabsEnum {
  DETAILS = 'details',
  PASSWORD = 'password',
}

export const Profile = () => {
  const location = useLocation();
  const { navigate } = useRouteHook();

  const [activeTab, setActiveTab] = useState<TabsEnum>(TabsEnum.DETAILS);
  const { isMobile } = useResponsive();
  const { t } = useTranslation('profile');

  useEffect(() => {
    if (location.pathname.endsWith('/password')) setActiveTab(TabsEnum.PASSWORD);
    else setActiveTab(TabsEnum.DETAILS);
  }, [location]);

  const handleTabChange = (value: TabsEnum) => {
    setActiveTab(value);
    if (value === TabsEnum.PASSWORD) {
      navigate(RoutePaths.profile.onNavigate({ section: 'password' }), { replace: true });
    } else {
      const basePath = location.pathname.split('/').slice(0, -1).join('/');
      navigate(basePath || '.', { replace: true });
    }
  };

  const tabs = useMemo(
    () => [
      { title: t('tabs.personalDetails'), value: TabsEnum.DETAILS, label: <PersonalSvg /> },
      { title: t('tabs.changePassword'), value: TabsEnum.PASSWORD, label: <PasswordSvg /> },
    ],
    [t],
  );
  return (
    <div
      className={clsx(
        'max-[840px]:overflow-x-hidden',
        PaletteUtils.isBordierPalette ? 'bg-white shadow-[0_2px_14px_0_rgba(0,0,0,0.21)]' : 'bg-[#fcfcfc]',
      )}
    >
      {isMobile && (
        <>
          {activeTab === TabsEnum.DETAILS && <DetailsEditor />}
          {activeTab === TabsEnum.PASSWORD && <Password />}
        </>
      )}
      {!isMobile && (
        <>
          <Tabs options={tabs} value={activeTab} onChange={handleTabChange} />
          <div>
            {activeTab === TabsEnum.DETAILS && <DetailsEditor />}
            {activeTab === TabsEnum.PASSWORD && <Password />}
          </div>
        </>
      )}
    </div>
  );
};
